import { memo } from 'react';

import { useShortIdContext } from '@air/provider-short-id';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { usePublicRootBoard } from '~/components/PublicRootBoardProvider';
import { PublicBoardDownloadMenu } from '~/components/Shared/HeaderBar/PublicBoardDownloadMenu';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

/**
 * The board topbar's "Save all" download menu. Split out of PublicBoardTopbar as its own leaf
 * because it is the ONLY consumer of PublicRootBoardProvider (a strict context that throws when the
 * provider is absent): the editor's inline edit canvas renders the board subtree under the
 * COLLECTION providers, which never mount PublicRootBoardProvider, and "Save all" is a viewer
 * action that doesn't belong in the editor anyway. Keeping the `usePublicRootBoard()` call in this
 * leaf lets the canvas suppress it by not rendering the leaf — rather than forcing an unconditional
 * root-board read (and throw) into the shared topbar.
 */
export const PublicBoardTopbarDownloadMenu = memo(() => {
  // The ROOT shared board (not the centralized/currently-viewed board): "Save all" downloads the
  // whole share, matching the prior header behavior — targeting the centralized sub-board would 403.
  const rootBoard = usePublicRootBoard();
  const { permissions } = usePublicPermissions();
  const { presentationSettings } = useShortIdContext();

  const canShowDownloadMenu = canGenerateZip(permissions) && !presentationSettings?.hideDownloadAll;

  if (!rootBoard || !canShowDownloadMenu) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-2">
      <PublicBoardDownloadMenu board={rootBoard} size="small" trackLocation="board-stats-bar" />
    </div>
  );
});

PublicBoardTopbarDownloadMenu.displayName = 'PublicBoardTopbarDownloadMenu';
